import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import NicknameBar from '../components/NicknameBar';

interface BondOption {
  id: string;
  text: string;
  resolvedText?: string;
}

interface BondVotingScreenProps {
  voteId: string;
  options: BondOption[];
  canVote: boolean;
  initialVotedOptionId?: string;
}

function BondVotingScreen({ voteId, options, canVote, initialVotedOptionId }: BondVotingScreenProps) {
  const [votedOptionId, setVotedOptionId] = useState<string | null>(initialVotedOptionId || null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [heroName, setHeroName] = useState<string>('');

  // Fetch hero name for display
  useEffect(() => {
    const fetchHeroInfo = async () => {
      try {
        const response = await fetch(`/api/bond-votes/${voteId}`);
        if (response.ok) {
          const data = await response.json();
          const sessionId = data.bondVote.sessionId;
          const fromTeam = data.bondVote.fromTeam;

          // Get the winning sheet from sheet votes
          const votesResponse = await fetch(`/api/sheet-votes/session/${sessionId}/latest`);
          if (votesResponse.ok) {
            const votesData = await votesResponse.json();
            const teamVote = votesData.votes?.find((v: any) => v.teamColor === fromTeam && v.finalSheetId);
            if (teamVote?.winnerSheet?.name) {
              setHeroName(teamVote.winnerSheet.name);
            }
          }
        }
      } catch (error) {
        console.error('[BondVotingScreen] Error fetching hero info:', error);
      }
    };

    fetchHeroInfo();
  }, [voteId]);

  const handleVote = async (optionId: string) => {
    if (!canVote || votedOptionId || isSubmitting) return;

    const audienceId = localStorage.getItem('audienceId');
    if (!audienceId) {
      toast.error('Sessione non valida');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`/api/bond-votes/${voteId}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ audienceId, optionId }),
      });

      if (response.ok) {
        setVotedOptionId(optionId);
        toast.success('Legame scelto!');
      } else {
        const error = await response.json();
        toast.error(error.error || 'Errore durante il voto');
      }
    } catch (error) {
      console.error('[BondVotingScreen] Error submitting vote:', error);
      toast.error('Errore di connessione');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen p-4 pt-10">
      <NicknameBar />
      {/* Header */}
      <div className="text-center py-4">
        <h1 className="text-2xl font-heading font-bold text-ink mb-1">Scegli il Legame</h1>
        {heroName && (
          <p className="text-xl text-team-yellow font-semibold">{heroName}...</p>
        )}
      </div>

      {/* Opzioni */}
      <div className="flex-1 flex flex-col space-y-3 max-w-md mx-auto w-full pt-2">
        {options.map((option) => {
          const isVoted = votedOptionId === option.id;
          const isDisabled = !canVote || (votedOptionId !== null && !isVoted) || isSubmitting;
          const displayText = option.resolvedText || option.text;

          return (
            <button
              key={option.id}
              onClick={() => handleVote(option.id)}
              disabled={isDisabled}
              className={`w-full py-5 px-6 rounded-xl text-lg font-semibold transition-all text-left ${
                isVoted
                  ? 'bg-team-yellow text-ink ring-4 ring-team-yellow scale-105'
                  : isDisabled
                  ? 'bg-parchment-300 text-ink-muted cursor-not-allowed'
                  : 'parchment-card text-ink hover:bg-parchment-200 active:scale-95'
              }`}
            >
              {displayText}
              {isVoted && <span className="ml-2">✓</span>}
            </button>
          );
        })}
      </div>

      {/* Stato voto */}
      {votedOptionId && (
        <div className="text-center py-4">
          <p className="text-team-green font-semibold">Legame scelto!</p>
        </div>
      )}

      {!canVote && !votedOptionId && (
        <div className="text-center py-4">
          <p className="text-ink-light">Votazione in corso...</p>
        </div>
      )}
    </div>
  );
}

export default BondVotingScreen;
